/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { Component } from 'react';
import ReactDOMServer from 'react-dom/server';
import { connect, ConnectedProps } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Loader } from '@g17eco/atoms/loader';
import { loadUNSDGMap } from '../../actions/common';
import { calculateChartData, SdgStackedColumnChart } from './chart/sdgCharts';
import { loadUniversalTrackerModal } from '../../actions/universalTrackerModal';
import { getSDGShortTitle } from '../../constants/sdg-data';
import { isUserManager } from '../../selectors/user';
import { RootState } from '../../reducers';
import { ScorecardGoal, ScorecardResponse, ScorecardTarget } from '../../types/scorecard';
import SDGIcon from '@g17eco/molecules/sdg-icon/sdg-icon';
import NumberFormat from '../../utils/number-format';
import { UNGoal, UNTarget } from '../../reducers/unsdg-map';
import { UniversalTrackerValueView } from '@features/universal-tracker-value';
import { generateUrl } from '../../routes/util';
import { ROUTES } from '../../constants/routes';
import { getQuestionOptionalValueListMap } from '../../selectors/survey';
import { loadSurvey } from '../../actions/survey';
import { filterSdg } from '@utils/survey/filters';
import { loadBlueprintMapping } from '../../actions/blueprints';
import IconButton from '@g17eco/molecules/button/IconButton';
import { getStatusIcon, getStatusText, isUtrvAssuranceComplete } from '../../utils/universalTrackerValue';
import './sdg-detail.scss';
import { tooltips } from '@features/impact-performance/SdgChart';
import { UniversalTrackerType } from '@g17eco/types/universalTracker';
import { QUESTION } from '@constants/terminology';
import { getAppSettings } from '../../selectors/appConfig';
import { getDisplayDecimalNumber } from '@utils/number';
import { Column, Divider, Row, TrackingList } from '@g17eco/molecules/tracking-list';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { UniversalTrackerValuePlain } from '@g17eco/types/universalTrackerValue';

interface SdgDetailViewProps extends PropsFromRedux {
  sdgCode: string | number;
  scorecard: ScorecardResponse
}

interface ContributionData {
  contribution: string | number;
  target: string | number;
  materiality: string;
  scope: string;
}

interface SdgDetailViewState {
  selectedTarget: string;
}

/** @TODO: should migrate this to function component
 * @deprecated loadBlueprintMapping, should migrate to use RTK blueprintsApi
 */
class SdgDetailView extends Component<SdgDetailViewProps & RouteComponentProps<{ initiativeId: string, surveyId?: string }>, SdgDetailViewState> {

  state = {
    selectedTarget: ''
  }

  componentDidMount() {
    this.props.loadUNSDGMap();
  }

  componentDidUpdate() {
    this.props.loadUNSDGMap();
    const { sdgCode } = this.props;
    const { surveyId } = this.props.match.params;
    if (surveyId && sdgCode) {
      const goal = `${sdgCode}`;
      if (goal) {
        this.props.loadSurvey(surveyId);
        this.props.loadBlueprintMapping();
      }
    }
  }

  handleTargetChange = (target: string) => {
    this.setState({ selectedTarget: target });
  }

  handleEditTarget = (utrId: string, utrvId: string | undefined = undefined) => {
    this.props.loadUTrModal(utrId, utrvId);
  }

  getGoalData = (goalScorecard: ScorecardGoal, initiative: ScorecardResponse['initiative'], sdgGoal: string | number): ContributionData => {
    const contribution = typeof goalScorecard.actual === 'number' ? getDisplayDecimalNumber(goalScorecard.actual) : 0;
    const target = typeof goalScorecard.target === 'number' ? getDisplayDecimalNumber(goalScorecard.target) : 0;

    const totalScope = goalScorecard.targets.length;
    const inScope = goalScorecard.targets.reduce((a, t) => a + (t.inScope ? 1 : 0), 0);

    let materialityTxt = 'None';
    if (initiative) {
      const materialityPc = initiative.materiality ? initiative.materiality[sdgGoal] : 0;
      materialityTxt = materialityPc > 66 ? 'High' : materialityPc > 33 ? 'Medium' : materialityPc > 0 ? 'Low' : 'None';
    }

    return {
      contribution,
      scope: `${inScope} of ${totalScope} Targets`,
      materiality: materialityTxt,
      target,
    };
  }

  getSDGColor(sdg: number) {
    const colours = [
      '#f1002f',
      '#d99e3c',
      '#219b52',
      '#c81035',
      '#f83631',
      '#00aed9',
      '#ffb437',
      '#921036',
      '#f56a2f',
      '#e60080',
      '#ff993a',
      '#d48a37',
      '#497744',
      '#007db8',
      '#3bb056',
      '#005586',
      '#0c3866'
    ];
    return colours[sdg - 1];
  }

  goToQuestion(id: string) {
    const { initiativeId, surveyId } = this.props.match.params;
    const url = `${generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId })}/question/${id}`;
    this.props.history.push(url);
  }

  getStatusIcon(utrv: Pick<UniversalTrackerValuePlain, 'status' | 'assuranceStatus' | 'lastUpdated'>) {
    const { initiativeId, surveyId } = this.props.match.params;

    if (!utrv) {
      return {
        icon: '',
        onClick: undefined,
        disabled: true,
        className: 'fixed-width',
        tooltip: '',
      }
    }

    const defaultIcon = {
      icon: getStatusIcon(utrv),
      onClick: undefined,
      disabled: true,
      className: 'fixed-width',
      tooltip: getStatusText(utrv),
    }

    if (isUtrvAssuranceComplete(utrv.assuranceStatus)) {
      return {
        ...defaultIcon,
        className: 'assured fixed-width',
        disabled: false,
        onClick: () => this.props.history.push(`${generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId })}/assurance`)
      };
    }

    return {
      ...defaultIcon,
      disabled: false,
      className: `${utrv.status} fixed-width`,
    };
  }

  renderHeader(data: UNGoal | UNTarget, score?: number) {
    const { sdgCode } = this.props;

    const contribution = score !== undefined ? <>
      <div className={`float-end header-info-value strong ${score === 0 ? 'text-ThemeHeadingLight' : score < 0 ? 'text-ThemeDangerMedium' : 'text-ThemeSuccessMedium'}`}>
        <NumberFormat value={score} suffix='%' decimalPlaces={0} emptyValue={'0%'} />
      </div></> : null

    return (
      <div className='header-info d-flex align-items-center'>
        <div className='header-info-icon pr-3 align-self-start'>
          <SDGIcon code={sdgCode} width={70} />
        </div>
        <div className='flex-fill pr-3'>
          {contribution}
          <h4>SDG {sdgCode}: {getSDGShortTitle(sdgCode)}</h4>
          <p>{data?.description}</p>
        </div>
      </div>
    );
  }

  render() {
    const { sdgCode, scorecard, UNSDGMapState, canShowSDGDetails } = this.props;

    if (!sdgCode) {
      // Need to have sdgCode to do anything
      return null;
    }

    if (!scorecard || !UNSDGMapState.loaded) {
      return <Loader />;
    }

    const goal = `${sdgCode}`;

    return canShowSDGDetails ? this.renderGoalInfo(goal) : this.renderContributingQuestions(goal);
  }

  renderContributingQuestions(goal: string) {
    const { sdgCode, scorecard: { scorecard }, UNSDGMapState, surveyState, surveyQuestionList, surveyBlueprintState } = this.props;
    const { surveyId } = this.props.match.params;

    const goalData = UNSDGMapState.data?.goals?.find(d => d.code === goal);
    const hasSurvey = Boolean(surveyId);

    if (!goalData) {
      return null;
    }

    const goalScorecard = scorecard.goals[Number(goal) - 1];
    const targetScorecard = goalScorecard?.targets.find(t => {
      const goal = t.sdgCode.split('.')[0];
      return goal === `${sdgCode}`
    });

    const targetData = goalData.targets.find(d => {
      const goal = d.code.split('.')[0];
      return goal === `${sdgCode}`
    });
    if (!targetData || !targetScorecard) {
      return null;
    }

    if (hasSurvey && (!surveyState.loaded || !surveyBlueprintState.loaded)) {
      return <Loader />;
    }

    const surveyBlueprint = surveyBlueprintState.data;
    const questionList = hasSurvey ? Array.from(surveyQuestionList.values())
      .filter((q) => surveyBlueprint ? filterSdg(q, surveyBlueprint, String(sdgCode)) : false) : [];

    return (
      <div className='sdg-detail-container'>
        {this.renderHeader(targetData)}
        {hasSurvey ?
          <div className='question-table mt-3'>
            <h4>{QUESTION.CAPITALIZED_PLURAL} that impact this SDG</h4>
            <TrackingList className='questionList mt-3'>
              {questionList.map((question, rowIndex) => {

                const universalTracker = question.universalTracker;
                if (!question.utrv) {
                  return <Row key={`${universalTracker.getId()}-${rowIndex}`}>
                    <Column stretch truncate>{universalTracker.getName()}</Column>
                  </Row>
                }

                const utrv = question.utrv;
                const id = question.utrv?._id;

                const goToQuestionClick = () => id && this.goToQuestion(id);

                const statusIcon = this.getStatusIcon(utrv);

                return (
                  <Row key={`${id}-${rowIndex}`}>
                    <Column stretch truncate>
                      {universalTracker.getName()}
                    </Column>
                    <Divider />
                    <Column className='valueCol'>
                      <UniversalTrackerValueView
                        utr={universalTracker}
                        utrv={question.utrv}
                        valueType={question.valueType}
                        showTableDownload={false}
                        className=''
                      />
                    </Column>
                    <Divider />
                    <Column className='text-center' tooltip={statusIcon.tooltip}>
                      <i className={`fa ${statusIcon.icon} ${statusIcon.className}`} />
                    </Column>
                    <Divider />
                    <Column className='text-center' tooltip={`Jump to ${QUESTION.SINGULAR}`} onClick={goToQuestionClick}>
                      <i className='fa fa-eye text-ThemeAccentMedium' />
                    </Column>
                  </Row>
                )
              })}
            </TrackingList>
          </div>
          : <h4 className='mt-4 strong text-center text-ThemeHeadingLight'>No figures can be shown for the Current Total (Automatic Report)</h4>
        }
      </div>
    );
  }

  renderGoalInfo(goal: string) {
    const { scorecard: { scorecard, initiative }, UNSDGMapState } = this.props;
    const { selectedTarget } = this.state;

    const sdgGoal = Number(goal);
    const unsdg = UNSDGMapState.data?.goals;

    const goalData = unsdg?.find(d => d.code === goal);
    const goalScorecard = scorecard.goals[sdgGoal - 1];

    if (!goalScorecard || !goalData) {
      return null;
    }

    const colour = this.getSDGColor(Number(goal));

    const currentSDGTargetDetails = selectedTarget ? goalData.targets.find(d => d.code === `${selectedTarget}`) : undefined;

    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    const targets = [...goalScorecard.targets].sort((a, b) => collator.compare(a.sdgCode, b.sdgCode));

    const contributionData = this.getGoalData(goalScorecard, initiative, sdgGoal);

    return (
      <div className='sdg-detail-container'>
        {this.renderHeader(goalData, goalScorecard.actual)}
        <div className='d-flex mb-3' style={{ minHeight: '500px' }}>
          <div className='sdg-chart'>
            {this.renderGoalBarChart(goalScorecard, colour, contributionData)}
            <div className={'sdg-label'}>
              <SDGIcon code={sdgGoal} />
              <h4>SDG {sdgGoal}</h4>
            </div>
          </div>
          <div className='sdg-chart ml-3'>
            {this.renderTargetBarChart(targets, colour)}
            <div className={'sdg-label'}>
              <SDGIcon code={sdgGoal} />
              <h4>Targets</h4>
            </div>
          </div>
          <div className='sdg-target-key d-flex flex-column'>
            {this.renderSDGTargetKey(targets)}
          </div>
          <div className='flex-grow-1 ml-5'>
            <h4 className='mt-2 mb-3'>{goalData.title}</h4>
            <p>{goalData.description}</p>
            <div className='my-3'>
              {this.renderContributionTable(contributionData, goalScorecard.utrId)}
            </div>
            {currentSDGTargetDetails ?
              <>
                <h4 className='mt-4 mb-3'>SDG {currentSDGTargetDetails.code}: {getSDGShortTitle(currentSDGTargetDetails.code)}</h4>
                <p>{currentSDGTargetDetails.description}</p>
                <div className='my-3'>
                  {this.renderTargetContributionTable(goalScorecard)}
                </div>
              </>
              : null}
          </div>
        </div>
      </div>
    );
  }

  renderSDGTargetKey = (targets: ScorecardTarget[]) => {
    const countOfTargets = targets.length;
    const heightOfBar = 490;
    const imageMargin = 1;
    const heightOfContainer = heightOfBar / countOfTargets;
    const widthOfIcon = Math.floor(heightOfContainer - (imageMargin * 2));

    const containerStyle = {
      height: `${heightOfContainer}px`,
    };

    return targets.reverse().map(target =>
      <div key={`sdg-target-key-${target.sdgCode}`} className={`d-flex ${target.inScope ? '' : 'out-of-scope'}`} style={containerStyle}>
        <SDGIcon
          width={widthOfIcon}
          maxWidth={'65px'}
          code={target.sdgCode}
          className={'clickable'}
          onClick={() => this.handleTargetChange(target.sdgCode)} />
      </div>
    );
  }

  renderTargetBarChart = (targets: ScorecardTarget[], colour: string) => {
    const chartTwo = calculateChartData(targets, colour);
    const targetData = { data: chartTwo.data, name: 'Targets', goalTicks: chartTwo.goalTicks };

    return SdgStackedColumnChart(targetData.data, targetData.goalTicks, undefined, this.handleTargetChange);
  }

  renderGoalBarChart = (goalScorecard: ScorecardGoal, colour: string, contributionData: ContributionData) => {
    const styleOne = 'color: ' + colour + '; opacity:0.8';
    const styleTwo = 'color: ' + colour + '; opacity:0.3';

    const roleStyle = { role: 'style' };
    const roleTooltip = { type: 'string', role: 'tooltip', p: { html: true } };
    const contribution = Math.round(10 * goalScorecard.actual || 0) / 10;
    const target = Math.round(10 * goalScorecard.target || 0) / 10;

    const tooltipContribution = this.getContributionTooltip(goalScorecard.sdgCode, contributionData);
    const tooltipTarget = this.getTargetTooltip(goalScorecard.sdgCode, contributionData);

    const chartOne = {
      data: [
        ['P', 'Complete', roleTooltip, roleStyle, 'Target', roleTooltip, roleStyle],
        ['',
          contribution, tooltipContribution, styleOne,
          target - contribution, tooltipTarget, styleTwo
        ],
      ],
      goalTicks: [{ v: 0, f: '0%' }, { v: contribution, f: `${Math.round(contribution)}%` }, { v: 100, f: '100%' }]
    }
    const goalData = { data: chartOne.data, name: 'SDG', goalTicks: chartOne.goalTicks };

    return SdgStackedColumnChart(goalData.data, goalData.goalTicks, undefined, this.handleTargetChange);
  }

  getContributionTooltip = (sdgCode: number | string, contributionData: ContributionData) => {
    const el = <div className='p-2' style={{ minWidth: '240px' }}>
      <div className='d-flex justify-content-between gap-1'>
        <strong>SDG {sdgCode} {getSDGShortTitle(sdgCode)} </strong>
        <div>{contributionData.contribution}</div>
      </div>
      <div className='row g-0'>
        <div className='col-6 text-right'>Contribution:</div>
        <div className='col-6 text-left pl-2'>{contributionData.contribution}%</div>
      </div>
      <div className='row g-0'>
        <div className='col-6 text-right'>Scope:</div>
        <div className='col-6 text-left pl-2'>{contributionData.scope}</div>
      </div>
      <div className='row g-0'>
        <div className='col-6 text-right'>Materiality:</div>
        <div className='col-6 text-left pl-2'>{contributionData.materiality}</div>
      </div>
    </div>;

    return ReactDOMServer.renderToString(el);
  }

  getTargetTooltip = (sdgCode: number | string, contributionData: ContributionData) => {
    const el = <div className='p-2' style={{ minWidth: '240px' }}>
      <div className='d-flex justify-content-between gap-1'>
        <strong>SDG {sdgCode} {getSDGShortTitle(sdgCode)} </strong>
        <div>{contributionData.target}</div>
      </div>
      <div className='row g-0'>
        <div className='col-6 text-right'>Target Contribution:</div>
        <div className='col-6 text-left pl-2'>{contributionData.target}%</div>
      </div>
    </div>
    return ReactDOMServer.renderToString(el);
  }

  renderContributionTable = (contributionData: ContributionData, utrId: string | undefined) => {
    const { isManager } = this.props;

    if (!utrId) {
      return null;
    }

    return (
      <table className='contribution-table'>
        <tbody>
          <tr>
            <th className='strong'>Company's contribution</th>
            <td>{contributionData.contribution}%</td>
            <td className='text-right'>
              <IconButton tooltip='View contribution history' className='mr-3' icon='fa-history' onClick={() => this.handleEditTarget(utrId)} />
              <SimpleTooltip text={tooltips.CONTRIBUTION}>
                <i className='fa fa-info-circle mr-2' />
              </SimpleTooltip>
            </td>
          </tr>
          <tr>
            <th className='strong'>Company's target</th>
            <td>{contributionData.target}%</td>
            <td className='text-right'>
              {isManager ?
                <IconButton tooltip='Edit this target' icon='fa-edit' onClick={() => this.handleEditTarget(utrId)} />
                : null
              }
            </td>
          </tr>
          <tr>
            <th className='strong'>Scope</th>
            <td>{contributionData.scope}</td>
            <td className='text-right'>
              <SimpleTooltip text={tooltips.SCOPE}>
                <i className='fa fa-info-circle mr-2' />
              </SimpleTooltip>
            </td>
          </tr>
          <tr>
            <th className='strong'>Materiality</th>
            <td>{contributionData.materiality}</td>
            <td className='text-right'>
              <SimpleTooltip text={tooltips.MATERIALITY}>
                <i className='fa fa-info-circle mr-2' />
              </SimpleTooltip>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  renderTargetContributionTable = (goalScorecard: ScorecardGoal) => {
    const { selectedTarget } = this.state;
    const targetScore = goalScorecard.targets.find(t => t.sdgCode === selectedTarget);

    return (
      <table className='contribution-table'>
        <tbody>
          <tr>
            <th className='strong'>Current SDG Target Contribution</th>
            <td>{isNaN(targetScore?.actual) ? 0 : Math.round(targetScore?.actual)}%</td>
            <td className='text-right'>
              <SimpleTooltip text={tooltips.CONTRIBUTION}>
                <i className='fa fa-info-circle mr-2' />
              </SimpleTooltip>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  const isManager = isUserManager(state);
  return {
    isManager,
    UNSDGMapState: state.UNSDGMap,
    surveyQuestionList: getQuestionOptionalValueListMap(state),
    surveyState: state.survey,
    surveyBlueprintState: state.blueprint,
    canShowSDGDetails: getAppSettings(state).canShowSDGDetails,
  }
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    loadUNSDGMap: () => dispatch(loadUNSDGMap()),
    loadUTrModal: (universalTrackerId: string, universalTrackerValueId: string | undefined) => dispatch(loadUniversalTrackerModal({
      universalTrackerId: universalTrackerId,
      universalTrackerType: UniversalTrackerType.Utr,
      actionValueId: universalTrackerValueId
    })),
    loadSurvey: (surveyId: string) => dispatch(loadSurvey(surveyId)),
    loadBlueprintMapping: () => dispatch(loadBlueprintMapping())
  }
};

const connectorFn = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connectorFn>
const connectedApp = connectorFn(SdgDetailView);
export default withRouter(connectedApp);
